/**
 * Reserved vendor-policy spec keys and the shared OpenRouter/vLLM request-body
 * projection. Policy lives on `spec.properties` under the reserved
 * `vendorPolicy` name so every runtime builds the same provider block instead
 * of hand-rolling ZDR bodies.
 */

import { Effect } from 'effect';

import type { ParserProperty, ParserSpec } from './parser';

export const VENDOR_POLICY_PROPERTY = 'vendorPolicy';

export interface VendorPolicy {
  readonly zdr?: boolean;
  readonly providerOrder?: readonly string[];
  readonly dataCollection?: 'allow' | 'deny';
  readonly sort?: string;
  readonly providerIgnore?: readonly string[];
  readonly allowFallbacks?: boolean;
}

export type VendorPolicySpec = Pick<ParserSpec, 'properties'>;

function isVendorPolicy(value: unknown): value is VendorPolicy {
  return typeof value === 'object' && value !== null;
}

/** Read the reserved `vendorPolicy` property default from a parser/vendor spec. */
export function vendorPolicyFromSpec(spec: VendorPolicySpec): VendorPolicy {
  const property = spec.properties.find(
    (candidate: ParserProperty) => candidate.name === VENDOR_POLICY_PROPERTY,
  );
  return property && isVendorPolicy(property.default) ? property.default : {};
}

/**
 * Apply the reserved policy to an OpenRouter/vLLM request body. Key order is
 * pinned (`zdr`, `data_collection`, `sort`, `order`, `ignore`,
 * `allow_fallbacks`) so recorded request bodies stay byte-identical.
 */
export function applyVendorPolicy<T extends Record<string, unknown>>(
  requestBody: T,
  spec: VendorPolicySpec,
): T & { readonly provider: Record<string, unknown> } {
  const policy = vendorPolicyFromSpec(spec);
  const provider: Record<string, unknown> = {};
  if (policy.zdr !== undefined) provider.zdr = policy.zdr;
  if (policy.dataCollection !== undefined) provider.data_collection = policy.dataCollection;
  if (policy.sort !== undefined) provider.sort = policy.sort;
  if (policy.providerOrder !== undefined) provider.order = [...policy.providerOrder];
  if (policy.providerIgnore !== undefined) provider.ignore = [...policy.providerIgnore];
  if (policy.allowFallbacks !== undefined) provider.allow_fallbacks = policy.allowFallbacks;
  return { ...requestBody, provider };
}

/** Effect variant for request builders already composing in an Effect channel. */
export const applyVendorPolicyEffect = Effect.fn('okrapdf/parser-sdk.applyVendorPolicy')(
  <T extends Record<string, unknown>>(requestBody: T, spec: VendorPolicySpec) =>
    Effect.sync(() => applyVendorPolicy(requestBody, spec)),
);
